import Ionicons from 'react-native-vector-icons/Ionicons';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Animated,
  Image,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import RichText from '../components/RichText';
import { useTheme } from '../context/ThemeContext';
import { usePlayer } from '../context/PlayerContext';
import { getThemeColors } from '../utils/themeStyles';
import { SceneBeat } from '../data/sceneTypes';
import { characterColor } from '../utils/characterColor';
import { getScenarioForMission, getRuntimeCharacter, getMissionPlayer, loadRuntimeScenario } from '../data/scenarios';
import { apiFetch } from '../api/client';
import { type Palette, TYPE_COLOR } from '../utils/cyb';
import { usePalette } from '../hooks/usePalette';
import ElementHeader from '../components/ElementHeader';
import HeaderNav from '../components/HeaderNav';
import { useElementVisited } from '../utils/elementProgress';
import { useVisitedItemNav } from '../hooks/useVisitedItemNav';
import { useLineAudio } from '../hooks/useLineAudio';
import { useRunAudioReady, useStreamedLineAudio } from '../hooks/useRunAudio';
import { useAuth } from '../context/AuthContext';
import { track } from '../insights/beacon';
import { useLiveness } from '../insights/liveness';
import { useT } from '../i18n/useT';
import DiagramViewer from '../components/DiagramViewer';

// In-memory seen scenes tracker (resets on app reload)
const seenScenes = new Set<string>();
// CCT-253 — la table d'avatars joueurs BUNDLÉS a disparu : le joueur parle en son nom,
// avec sa photo de profil, et le personnage incarné vient du scénario servi.
// ─── Player override for dynamic Raoul avatar/name ────────────────────────────
let _playerName = 'Joueur';
// Nom du PERSONNAGE incarné par le joueur (désigné dans le Story Builder, CCT-152). Ses
// répliques sont celles du joueur : elles portent son identité et passent à DROITE. Le nom
// « Raoul » codé en dur ne valait que pour l'histoire bundlée d'origine.
let _playerCharName: string | null = null;
// Ouverture de la FICHE d'un personnage depuis un dialogue (CCT-153). Passe par une variable
// de module : les beats sont rendus par des fonctions hors composant, sans accès au state.
let _openCharCard: ((rawName: string) => void) | null = null;
/** Vrai si ce locuteur EST le joueur (personnage incarné, ou héritage « Raoul »). */
const isPlayerSpeaker = (raw: string) => {
  const me = _playerCharName
    ?? (getMissionPlayer(_missionId)?.name ?? null);   // relecture tardive : le scénario a pu arriver après
  return (!!me && raw.trim().toLowerCase() === me.trim().toLowerCase()) || raw === 'Raoul';
};
let _playerAvatar: any = null;
let _currentStoryKey = 'egocrisis';
let _missionId = 0;
// Élément joué et jeton courants — nécessaires pour demander l'audio d'une réplique dite À LA
// LECTURE (CCT-310). Au niveau du module, comme `_playerName` et `_missionId` : les faire
// descendre en props traverserait trois composants qui n'en ont aucun autre usage.
let _streamItemId = '';
let _streamToken: string | null = null;

// Visuel d'un personnage (image + couleur). SOURCE UNIQUE : le scénario servi (CCT-253).
// Il y avait ici trois niveaux de repli sur des avatars BUNDLÉS ; ils gagnaient sur la photo
// choisie par l'auteur dans la Gallery et masquaient toute erreur de résolution côté serveur.
// Sans photo, l'appelant affiche l'initiale colorée — un repli qui SE VOIT.
function resolveCharacterVisual(rawName: string): { img: any; color: string } {
  if (isPlayerSpeaker(rawName)) {
    // Le joueur parle en son nom : avatar de PROFIL, jamais celui d'un personnage.
    return { img: _playerAvatar || undefined, color: characterColor(rawName) };
  }
  const rc = getRuntimeCharacter(_missionId, rawName);
  return {
    img: rc?.avatarUrl ? { uri: rc.avatarUrl } : undefined,
    color: rc?.color || characterColor(rawName),
  };
}

/**
 * Beats a partir de la forme STRUCTUREE (`content.items[]`).
 *
 * Rien a deviner ici, contrairement au markdown : le type, le personnage et le texte sont
 * donnes. On transporte en plus l'AUDIO — cle du fichier et duree — qui pilotera la lecture
 * et l'enchainement.
 */
// ENCHAINEMENT AUTOMATIQUE DE LA SCENE (CCT-226)
//
// Une scene se REGARDE. Cliquer entre chaque replique casse exactement ce qu'on vient de
// construire : le texte parait entier, la voix le dit, et le lecteur devait quand meme
// appuyer. L'enchainement suit donc le rythme de la scene.
//
// ⚠️ DEUX CADENCES, PARCE QUE DEUX SITUATIONS.
//   * Replique DOUBLEE PAR LA VOIX : elle se termine quand le son se termine. Il ne reste
//     qu'une respiration, sinon les repliques se collent et le dialogue perd son souffle.
//   * NARRATION (hors perimetre du TTS, decision produit) : personne ne la lit a voix haute.
//     Enchainer des la fin de la frappe la rendrait illisible — le texte s'affiche en
//     quelques dixiemes de seconde et disparaitrait aussitot. On laisse le temps de LIRE.
// ⚠️ ENCHAINEMENT DIRECT ENTRE REPLIQUES PARLEES (precision de l'auteur : « passer
// directement a la replique suivante »). La replique est deja affichee en ENTIER et la voix
// l'a dite : attendre davantage ne fait rien lire de plus, ça ne fait qu'attendre. Le delai
// restant sert uniquement a ne pas coller la fin d'un souffle au debut du suivant.
const RESPIRATION_MS = 180;              // entre deux repliques parlees
// ⚠️ CADENCE DIVISEE PAR TROIS (demande produit). Les valeurs d'origine — 280 ms/mot, 2200 ms
// de plancher — visaient un rythme de lecture confortable pour qui DECOUVRE le texte. A
// l'usage l'attente paraissait longue : le texte est deja affiche en entier quand le decompte
// commence, et le joueur peut de toute façon appuyer pour avancer. On garde la MEME forme
// (proportionnelle au nombre de mots, avec un plancher) : c'est elle qui evite qu'une ligne
// courte disparaisse aussitot. Seule l'echelle change.
const LECTURE_PAR_MOT_MS = 93;           // ~645 mots/minute — 280/3
const LECTURE_MINIMALE_MS = 733;         // meme une ligne courte doit rester affichee — 2200/3

/**
 * Temps d'affichage APRES la fin du texte (ou du son) d'un beat.
 *
 * ⚠️ LA DECISION SE PREND SUR CE QUI S'EST PASSE, PAS SUR CE QUI ETAIT PREVU. Une replique
 * PORTE une cle audio ; cela ne dit pas que le son a ete ENTENDU. Un fichier introuvable,
 * une lecture refusee, et l'enchainement partait quand meme a la vitesse d'une replique
 * parlee : le texte defilait sans que rien ne soit dit ni lisible — « l'auto play a zappe
 * des repliques sans qu'elles soient lues ».
 *
 * Quand le son n'a pas joue, le beat redevient du TEXTE A LIRE, et prend le temps qu'il
 * faut. Une degradation doit rendre l'screen moins riche, jamais inutilisable.
 */
export function tempsAvantSuite(beat: { text?: string; audio?: { key?: string } } | undefined,
                                enLecture: boolean,
                                audioJoue = true): number {
  if (!beat) return 0;
  if (beat.audio?.key && audioJoue) return RESPIRATION_MS;
  if (!enLecture) return RESPIRATION_MS;
  const mots = String(beat.text || '').trim().split(/\s+/).filter(Boolean).length;
  return Math.max(LECTURE_MINIMALE_MS, mots * LECTURE_PAR_MOT_MS);
}

/**
 * Signale un beat fige a la telemetrie.
 *
 * ⚠️ UN ECRAN QUI SE FIGE NE PRODUIT AUCUN EVENEMENT. Sur la session analysee : 218
 * evenements, 0 erreur HTTP, 0 erreur console — parce qu'il ne se passe rien. Une panne
 * silencieuse est invisible par construction ; il faut donc l'emettre explicitement.
 */
function journaliserBlocage(index: number, total: number, type?: string): void {
  try {
    // ⚠️ ON PASSE PAR `track`, l'API REELLE du beacon. Une premiere version visait un
    // `window.__ccBeacon` qui n'existe pas : l'anomalie n'aurait jamais ete emise, et le
    // garde-fou aurait eu l'air de tracer alors qu'il ne tracait rien — un silence de plus
    // par-dessus le silence qu'il devait justement rompre.
    track({ type: 'anomalie', action: 'beat_fige', path: '/SceneReader',
            meta: { index, total, beat: type ?? null } });
  } catch {
    /* la telemetrie ne doit jamais casser la lecture */
  }
}

/** Fond de bulle en thème CLAIR, TEINTÉ par la couleur du personnage — CCT-279.
 *
 * ⚠️ BLANC SUR BLANC NE SE VOIT PAS. Les bulles étaient `#FFFFFF` sur un fond clair : sur
 * l'écran de fin de scène, où plusieurs répliques se suivent, on ne distinguait plus une
 * bulle de l'autre ni du fond — la conversation devenait un bloc de texte.
 *
 * Le contraste vient de la MÊME couleur que le liseré néon du personnage, très diluée : la
 * bulle reste presque blanche, mais elle se détache, et l'œil relie immédiatement la bulle à
 * son locuteur. Une teinte grise neutre aurait donné du contraste sans porter cette
 * information. */
function bubbleBackground(characterColor: string, dark: boolean): string {
  if (dark) return '#1E293B';
  // 14 % de la teinte du personnage sur blanc : assez pour séparer, trop peu pour fatiguer.
  return characterColor + '24';
}


function itemsEnBeats(items: any[]): SceneBeat[] {
  const beats: SceneBeat[] = [];
  for (let i = 0; i < (items || []).length; i += 1) {
    const it = items[i];
    const texte = String(it?.text ?? '').trim();
    if (!texte) continue;
    const audio = it?.audio && it.audio.statut === 'ok' && it.audio.key
      ? { key: String(it.audio.key), duree: Number(it.audio.duree_s) || 0 }
      : undefined;
    // RÉPLIQUE À DIRE À LA LECTURE (CCT-310) : le serveur la marque plutôt que de lui donner
    // une clé, parce que son texte change pendant la partie. Le CHEMIN suit le beat — c'est lui
    // qu'on renverra au serveur, jamais le texte.
    const audioStream = !audio && it?.audioStream === true;
    const audioPath = audioStream ? `items.${i}.text` : undefined;
    // DÉCOR EN VIGUEUR à ce beat (CCT-313). Le serveur a DÉJÀ fait la marche d'héritage : on ne
    // recalcule rien ici, on transporte. Un item `photo` ne devient pas un beat (il n'a pas de
    // `text`, il est écarté plus haut) — c'est bien un décor, pas une réplique.
    const backdropUrl = it?.backdropUrl ?? null;
    // CCT-477 — the diagram travels WITH its line, so it lands above exactly the one the author
    // attached it to. The server normalises the authoring spelling to `diagramUrl` beforehand.
    const diagramUrl = typeof it?.diagramUrl === 'string' ? it.diagramUrl : undefined;
    if (it?.kind === 'dialogue') {
      const nom = String(it.character ?? '').trim();
      beats.push({ type: 'dialogue', character: nom, side: 'left', text: texte,
                   audio, audioStream, audioPath, backdropUrl, diagramUrl });
    } else if (it?.kind === 'action') {
      beats.push({ type: 'action', text: texte, audio, audioStream, audioPath, backdropUrl, diagramUrl });
    } else {
      beats.push({ type: 'narration', text: texte, audio, audioStream, audioPath, backdropUrl, diagramUrl });
    }
  }
  return beats;
}

function toScenarioBeats(narrativeText: string): SceneBeat[] {
  const lines = narrativeText
    .split('\n')
    .map(l => l.trim())
    .filter(Boolean);
  const beats: SceneBeat[] = [];

  const pushAction = (text: string) => {
    const cleaned = text.trim().replace(/^\*|\*$/g, '').trim();
    if (cleaned.length > 0) beats.push({ type: 'action', text: cleaned });
  };

  for (const line of lines) {
    if (line.startsWith('**') && line.includes(':**')) {
      const marker = line.indexOf(':**');
      const name = line.slice(2, marker).trim();
      let remainder = line.slice(marker + 3).trim();

      // Split cinematic/action fragment from spoken line when format is:
      // **Name :** *Action* "Dialogue"
      const actionMatch = remainder.match(/^\*(.+?)\*\s*(.*)$/);
      if (actionMatch) {
        pushAction(`${name} : ${actionMatch[1]}`);
        remainder = actionMatch[2].trim();
      }

      let spoken = remainder;
      const quotedMatch = remainder.match(/"([^"]+)"/);
      if (quotedMatch) {
        spoken = quotedMatch[1].trim();
      } else {
        spoken = spoken.replace(/^"|"$/g, '').trim();
      }

      if (spoken.length > 0) {
        beats.push({
          type: 'dialogue',
          character: name,
          side: 'left',
          text: spoken,
        });
      }
      continue;
    }

    // Full italic line is treated as cinematic/action.
    if (line.startsWith('*') && line.endsWith('*')) {
      pushAction(line);
      continue;
    }

    const cleanedNarration = line.replace(/^\*|\*$/g, '').trim();
    if (cleanedNarration.length > 0) {
      beats.push({ type: 'narration', text: cleanedNarration });
    }
  }
  return beats;
}

function getScenarioSceneData(
  missionId?: number,
  chapterId?: number,
  sceneNumber?: number,
  itemId?: string | number,
) {
  if (!missionId) return undefined;
  const scenario = getScenarioForMission(missionId);
  const chapters = scenario?.chapters ?? [];
  if (!chapters.length) return undefined;

  // DEUX FORMES DE SCENE, et une seule etait acceptee (CCT-226).
  //
  //   * `content.items[]`        — forme STRUCTUREE, celle que le serveur envoie pour une
  //                                sequence : un item par replique, avec son personnage et,
  //                                depuis le TTS, sa cle audio et sa duree ;
  //   * `content.narrativeText`  — markdown d'un seul tenant, redecoupe ici par expression
  //                                reguliere (forme des cartes de type `story`).
  //
  // ⚠️ N'accepter que la seconde faisait REJETER toute scene venant de la base : l'screen
  // repliait alors sur le scenario BUNDLE, en silence. L'en-tete affichait le bon titre (il
  // vient des parametres) au-dessus du texte d'une autre histoire — et aucun audio ne
  // pouvait apparaitre, puisque la forme qui le porte n'etait jamais lue.
  const utilisable = (it: any) =>
    it && it.locked !== true && typeof it.content === 'object'
    && ('narrativeText' in it.content || Array.isArray(it.content.items));

  /** Beats d'un item de scene, quelle que soit sa forme. */
  const enBeats = (contenu: any): SceneBeat[] =>
    Array.isArray(contenu?.items)
      ? itemsEnBeats(contenu.items)
      : toScenarioBeats(String(contenu?.narrativeText ?? ''));

  // 1) Par IDENTIFIANT d'élément — la seule désignation stable.
  //    La recherche se faisait par (rang de chapitre, n° de scène) : deux compteurs POSITIONNELS
  //    calculés ailleurs, dans un autre repère. Dès qu'ils ne tombaient pas juste, la scène
  //    n'était pas trouvée et l'écran repliait sur une scène CODÉE EN DUR d'une autre histoire —
  //    l'en-tête affichait le bon titre (il vient des paramètres) au-dessus du mauvais texte.
  const target = itemId != null ? String(itemId) : '';
  if (target) {
    for (const ch of chapters) {
      const found = (ch.sequence ?? []).find((it: any) => String(it.id) === target);
      if (utilisable(found)) {
        return {
          id: sceneNumber ?? 1,
          title: (found as any).title,
          location: scenario?.title ?? 'Scenario',
          beats: enBeats((found as any).content),
          itemId: String((found as any).id ?? ''),
          // ⚠️ POSÉ PAR LE SERVEUR SUR L'ÉLÉMENT, pas déduit ici. Lui seul sait si le rendu de
          // la partie est terminé ; le redéduire des beats donnerait « prêt » dès qu'une seule
          // réplique porte une clé.
          audioPending: (found as any).audioPending === true,
          // Décor de la scène AVANT le premier beat (CCT-313) : sans lui l'écran s'ouvre nu et
          // le décor n'apparaîtrait qu'au beat suivant — ça se lit comme un raté d'affichage.
          backdropUrl: (found as any).content?.backdropUrl ?? null,
        };
      }
    }
    return undefined;
  }

  // 2) Repli POSITIONNEL, pour les appels qui ne transportent pas d'`itemId`.
  if (!sceneNumber) return undefined;
  const chapter = chapters[(chapterId ?? 1) - 1] ?? chapters[0];
  const sceneItem = (chapter?.sequence ?? []).filter(i => i.type === 'story')[sceneNumber - 1];
  if (!utilisable(sceneItem)) return undefined;
  return {
    id: sceneNumber,
    title: sceneItem.title,
    location: scenario?.title ?? 'Scenario',
    beats: enBeats((sceneItem as any).content),
    itemId: String((sceneItem as any).id ?? ''),
    audioPending: (sceneItem as any).audioPending === true,
    backdropUrl: (sceneItem as any).content?.backdropUrl ?? null,
  };
}

// ─── Beat renderers ───────────────────────────────────────────────────────────

// "Dialogue box" cinematic: reveal the line CHARACTER BY CHARACTER, with a longer pause after
// strong punctuation. That rhythm is what makes a line feel *played*, not printed. Muted only:
// under voice the text stays whole (CCT-226) — the voice already carries the tempo.
const TYPE_CHAR_MS = 18;            // base per visible character (~55 chars/s)
const TYPE_PAUSE_STRONG_MS = 230;  // after . ! ? … :
const TYPE_PAUSE_SOFT_MS = 90;     // after , ;
const PUNCT_STRONG = new Set(['.', '!', '?', '…', ':']);
const PUNCT_SOFT = new Set([',', ';']);
// Hide a just-typed, not-yet-closed emphasis marker at the reveal frontier, so a lone `*`/`**`
// never flashes on screen mid-reveal (RichText renders an unpaired marker literally).
const trimDanglingMarkup = (s: string) => s.replace(/\*{1,2}$/, '');

function TypewriterText({
  text,
  style,
  onComplete,
  onTexteAffiche,
  skipRef,
  audio,
  streamPath,
}: {
  text: string;
  style: any;
  onComplete: (audioJoue: boolean) => void;
  /**
   * The whole line is now READABLE — which is NOT the same event as `onComplete`.
   *
   * ⚠️ ONE FLAG USED TO CARRY BOTH FACTS, and it cost the player the scene. A voiced line is
   * displayed in full from the first frame, but `onComplete` waits for the end of the sound:
   * MEASURED on Ego Crisis 2, 12 voiced lines out of 26, one of 14.55 s and one of 12.96 s.
   * For ~12.7 s the player faced a text they had finished reading, with no chevron (it is
   * gated on the same flag) and an inert tap (it only wrote a ref, which re-renders nothing).
   */
  onTexteAffiche?: () => void;
  skipRef: React.MutableRefObject<boolean>;
  audio?: { key: string; duree: number };
  /** Chemin de la réplique quand elle se fait dire À LA LECTURE (`runtime`, CCT-310). */
  streamPath?: string;
}) {
  // Array.from, not split(''), so accented letters and emoji (surrogate pairs) count as one.
  const chars = Array.from(text);
  const total = chars.length;

  // RÉPLIQUE DITE À LA LECTURE (CCT-310). Son texte change pendant la partie (`$US.status`) :
  // elle n'a donc pas de clé dans le document, on la demande au serveur au moment de la jouer.
  //
  // ⚠️ AUCUN CACHE ICI. C'est le serveur qui sait si le statut a bougé — il garde l'audio par
  // empreinte du texte résolu, si bien qu'un rejeu ne lui coûte rien. Un cache local ferait
  // rejouer une phrase devenue fausse, exactement ce que ce genre existe pour éviter.
  const cleStream = useStreamedLineAudio(
    _streamToken, _missionId, _streamItemId, streamPath,
    !!streamPath && !skipRef.current);
  // `undefined` = on ne sait pas encore ; `''` = elle ne sera pas doublée. Les deux ne se
  // traitent pas pareil : l'un fait patienter le temps d'un aller-retour, l'autre fait jouer.
  const enAttenteDuFlux = !!streamPath && !skipRef.current && cleStream === undefined;
  const cleEffective = audio?.key || cleStream || '';

  // AUDIO DE LA REPLIQUE (CCT-226). Quand il existe, il commande DEUX choses :
  //
  //   * l'affichage — la replique parait ENTIERE des le debut. On lit en ecoutant, on ne
  //     dechiffre pas mot a mot une phrase deja prononcee. La frappe progressive n'avait de
  //     sens que muette, pour donner un rythme au silence ; sous la voix elle ne fait que
  //     retarder le texte sur ce qu'on entend deja ;
  //   * l'enchainement — c'est la FIN DU SON qui deverrouille la suite, pas la fin du texte.
  const aDeLAudio = !!cleEffective && !skipRef.current;
  // Kept in a ref so a new closure on every render does not re-arm the effect below.
  const afficheRef = useRef(onTexteAffiche);
  afficheRef.current = onTexteAffiche;
  const annonce = useRef(false);
  // ⚠️ SI L'AUDIO ECHOUE, L'ECRAN REPREND LA MAIN. Sans ce drapeau la scene restait figee :
  // « Suivant » attendait une fin de lecture qui n'arrivait jamais.
  const echecAudio = useRef(false);
  const [visibleCount, setVisibleCount] = useState(
    skipRef.current || aDeLAudio ? total : 0,
  );

  useLineAudio(aDeLAudio ? cleEffective : undefined,
                   aDeLAudio ? () => onComplete(true) : undefined, echecAudio,
                   audio?.duree ?? 0);

  useEffect(() => {
    // ⚠️ START NOTHING UNTIL THE STREAM HAS ANSWERED. Beginning the reveal and then laying the
    // voice over it would speak a line the player has already read; and calling `onComplete` here
    // would unlock « Suivant » before the first syllable. The text stays shown in full during the
    // round-trip — the player reads, they do not wait in front of nothing.
    if (enAttenteDuFlux) {
      setVisibleCount(total);
      return;
    }
    // Full text at once: line voiced by TTS, or a beat already seen (going back).
    if (aDeLAudio || skipRef.current) {
      setVisibleCount(total);
      // With voice, `onComplete` comes from the end of the sound — calling it here would unlock
      // « Suivant » before the first syllable.
      if (!aDeLAudio) onComplete(false);
      return;
    }
    // Muted: reveal CHARACTER BY CHARACTER, pausing after punctuation. A recursive setTimeout
    // (not a fixed setInterval) is what lets the delay vary per character — the cinematic rhythm.
    setVisibleCount(0);
    let idx = 0;
    let timer: ReturnType<typeof setTimeout>;
    const step = () => {
      if (skipRef.current) { setVisibleCount(total); onComplete(false); return; }
      idx++;
      setVisibleCount(idx);
      if (idx >= total) { onComplete(false); return; }
      const ch = chars[idx - 1];
      const delay = PUNCT_STRONG.has(ch) ? TYPE_PAUSE_STRONG_MS
                  : PUNCT_SOFT.has(ch) ? TYPE_PAUSE_SOFT_MS
                  : TYPE_CHAR_MS;
      timer = setTimeout(step, delay);
    };
    timer = setTimeout(step, TYPE_CHAR_MS);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, enAttenteDuFlux, aDeLAudio]);

  // ⚠️ ANNOUNCED FROM `visibleCount`, the only thing that KNOWS what is on screen. Deriving it
  // in the screen instead ("a beat with an audio key shows its text at once") would be a second
  // definition of the same fact, and the two would drift the day the reveal rule changes.
  useEffect(() => { annonce.current = false; }, [text]);
  useEffect(() => {
    if (total > 0 && visibleCount >= total && !annonce.current) {
      annonce.current = true;
      afficheRef.current?.();
    }
  }, [visibleCount, total]);

  // Filet de securite : un son illisible ne doit pas laisser la scene bloquee.
  useEffect(() => {
    if (!aDeLAudio) return;
    // ⚠️ `false` : le son a ECHOUE. C'est cette valeur qui rend au beat son temps de lecture
    // au lieu de l'expedier a la vitesse d'une replique parlee.
    const t = setInterval(() => {
      if (echecAudio.current) { onComplete(false); clearInterval(t); }
    }, 250);
    return () => clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  // ⚠️ THE VISIBLE PREFIX IS RENDERED, NOT THE WHOLE TEXT. Emphasis inside what is already
  // typed is honoured; a `**` still waiting for its pair stays literal until the closing one
  // arrives — self-correcting as the line completes, and never a marker left on screen.
  return <RichText style={style} value={trimDanglingMarkup(chars.slice(0, visibleCount).join(''))} />;
}

// Blinking ▼ "tap to continue" cue — the Zelda tell that a line has finished typing and the
// scene is waiting for the player, rather than silently sitting on a completed line.
function AdvanceChevron({ color }: { color: string }) {
  const blink = useRef(new Animated.Value(0.25)).current;
  useEffect(() => {
    const loop = Animated.loop(Animated.sequence([
      Animated.timing(blink, { toValue: 1, duration: 480, useNativeDriver: Platform.OS !== 'web' }),
      Animated.timing(blink, { toValue: 0.25, duration: 480, useNativeDriver: Platform.OS !== 'web' }),
    ]));
    loop.start();
    return () => loop.stop();
  }, [blink]);
  return (
    <Animated.View style={{ opacity: blink }}>
      <Text style={{ color, fontSize: 15, lineHeight: 15, fontWeight: '700' }}>▼</Text>
    </Animated.View>
  );
}

function NarrationBeatAnimated({ beat, dark, onComplete, onTexteAffiche, skipRef }: { beat: SceneBeat; dark: boolean; onComplete: (audioJoue: boolean) => void; onTexteAffiche?: () => void; skipRef: React.MutableRefObject<boolean> }) {
  return (
    <View style={[nb.wrap, { borderLeftColor: dark ? '#4B5563' : '#D1D5DB', backgroundColor: dark ? '#1F2937' : '#F9FAFB' }]}>
      <TypewriterText
        text={beat.text}
        audio={beat.audio}
        streamPath={beat.audioStream ? beat.audioPath : undefined}
        style={[nb.text, { color: dark ? '#D1D5DB' : '#374151' }]}
        onComplete={onComplete}
        onTexteAffiche={onTexteAffiche}
        skipRef={skipRef}
      />
    </View>
  );
}

function ActionBeatAnimated({ beat, dark, onComplete, onTexteAffiche, skipRef }: { beat: SceneBeat; dark: boolean; onComplete: (audioJoue: boolean) => void; onTexteAffiche?: () => void; skipRef: React.MutableRefObject<boolean> }) {
  return (
    <View style={[ab.wrap]}>
      <TypewriterText
        text={`[ ${beat.text} ]`}
        audio={beat.audio}
        streamPath={beat.audioStream ? beat.audioPath : undefined}
        style={[ab.text, { color: dark ? '#9CA3AF' : '#6B7280' }]}
        onComplete={onComplete}
        onTexteAffiche={onTexteAffiche}
        skipRef={skipRef}
      />
    </View>
  );
}

function DialogueBeatAnimated({ beat, dark, onComplete, onTexteAffiche, skipRef }: { beat: SceneBeat; dark: boolean; onComplete: (audioJoue: boolean) => void; onTexteAffiche?: () => void; skipRef: React.MutableRefObject<boolean> }) {
  const rawName = beat.character ?? '';
  const charName = isPlayerSpeaker(rawName) ? _playerName : rawName;
  // Le joueur parle à DROITE, comme dans un fil de discussion : à gauche, on ne distinguait
  // plus sa réplique de celle d'un PNJ.
  const side = isPlayerSpeaker(rawName) ? 'right' : (beat.side ?? 'left');
  const isLeft = side === 'left';
  const { img, color } = resolveCharacterVisual(rawName);
  const [imgError, setImgError] = useState(false);
  const avatarSize = 72;
  const bubbleBg = bubbleBackground(color, dark);
  // Liseré plus AFFIRMÉ en clair : à 55 d'opacité il s'évanouissait sur fond pâle.
  const bubbleBorder = color + (dark ? '55' : 'AA');

  return (
    <View style={[db.row, isLeft ? db.rowLeft : db.rowRight]}>
      <TouchableOpacity activeOpacity={0.8} onPress={() => _openCharCard?.(rawName)}
        style={[db.charCol, isLeft ? db.charColLeft : db.charColRight]}>
        <View style={[db.avatarWrap, { width: avatarSize, height: avatarSize, borderRadius: avatarSize / 2, borderColor: color }]}>
          {img && !imgError ? (
            <Image source={img} onError={() => setImgError(true)} style={{ width: avatarSize, height: avatarSize, borderRadius: avatarSize / 2 }} resizeMode="cover" />
          ) : (
            <View style={[db.avatarFallback, { backgroundColor: color, width: avatarSize, height: avatarSize, borderRadius: avatarSize / 2 }]}>
              <Text style={db.avatarInitial}>{charName[0] ?? '?'}</Text>
            </View>
          )}
        </View>
        <Text style={[db.name, { color: color }]}>{charName}</Text>
        {beat.expression ? <Text style={[db.expression, { color: dark ? '#9CA3AF' : '#6B7280' }]}>({beat.expression})</Text> : null}
      </TouchableOpacity>
      <View style={[db.bubble, { backgroundColor: bubbleBg, borderColor: bubbleBorder, borderTopLeftRadius: isLeft ? 4 : 16, borderTopRightRadius: isLeft ? 16 : 4 }, isLeft ? db.bubbleLeft : db.bubbleRight]}>
        <TypewriterText
          text={beat.text}
          audio={beat.audio}
          streamPath={beat.audioStream ? beat.audioPath : undefined}
          style={[db.bubbleText, { color: dark ? '#E5E7EB' : '#1F2937' }]}
          onComplete={onComplete}
          onTexteAffiche={onTexteAffiche}
          skipRef={skipRef}
        />
      </View>
    </View>
  );
}

const nb = StyleSheet.create({
  wrap: { borderLeftWidth: 3, paddingLeft: 16, paddingVertical: 12, marginVertical: 8, borderRadius: 4, borderTopLeftRadius: 0, borderBottomLeftRadius: 0 },
  text: { fontSize: 15, fontStyle: 'italic', lineHeight: 24 },
});

function ActionBeat({ beat, dark }: { beat: SceneBeat; dark: boolean }) {
  return (
    <View style={[ab.wrap]}>
      <RichText style={[ab.text, { color: dark ? '#9CA3AF' : '#6B7280' }]} value={`[ ${beat.text} ]`} />
    </View>
  );
}
const ab = StyleSheet.create({
  wrap: { alignItems: 'center', marginVertical: 8 },
  text: { fontSize: 13, fontStyle: 'italic' },
});

function DialogueBeat({
  beat,
  dark,
  large,
}: {
  beat: SceneBeat;
  dark: boolean;
  large?: boolean;
}) {
  const rawName = beat.character ?? '';
  const charName = isPlayerSpeaker(rawName) ? _playerName : rawName;
  // Le joueur parle à DROITE, comme dans un fil de discussion : à gauche, on ne distinguait
  // plus sa réplique de celle d'un PNJ.
  const side = isPlayerSpeaker(rawName) ? 'right' : (beat.side ?? 'left');
  const isLeft = side === 'left';
  const { img, color } = resolveCharacterVisual(rawName);
  const [imgError, setImgError] = useState(false);

  const avatarSize = large ? 72 : 44;
  const bubbleBg = bubbleBackground(color, dark);
  // Liseré plus AFFIRMÉ en clair : à 55 d'opacité il s'évanouissait sur fond pâle.
  const bubbleBorder = color + (dark ? '55' : 'AA');

  return (
    <View style={[db.row, isLeft ? db.rowLeft : db.rowRight]}>
      {/* Avatar column */}
      <TouchableOpacity activeOpacity={0.8} onPress={() => _openCharCard?.(rawName)}
        style={[db.charCol, isLeft ? db.charColLeft : db.charColRight]}>
        <View style={[db.avatarWrap, { width: avatarSize, height: avatarSize, borderRadius: avatarSize / 2, borderColor: color }]}>
          {img && !imgError ? (
            <Image source={img} onError={() => setImgError(true)} style={{ width: avatarSize, height: avatarSize, borderRadius: avatarSize / 2 }} resizeMode="cover" />
          ) : (
            <View style={[db.avatarFallback, { backgroundColor: color, width: avatarSize, height: avatarSize, borderRadius: avatarSize / 2 }]}>
              <Text style={db.avatarInitial}>{charName[0] ?? '?'}</Text>
            </View>
          )}
        </View>
        <Text style={[db.name, { color: color }]}>{charName}</Text>
        {beat.expression ? (
          <Text style={[db.expression, { color: dark ? '#9CA3AF' : '#6B7280' }]}>({beat.expression})</Text>
        ) : null}
      </TouchableOpacity>

      {/* Bubble */}
      <View
        style={[
          db.bubble,
          {
            backgroundColor: bubbleBg,
            borderColor: bubbleBorder,
            borderTopLeftRadius: isLeft ? 4 : 16,
            borderTopRightRadius: isLeft ? 16 : 4,
          },
          isLeft ? db.bubbleLeft : db.bubbleRight,
        ]}
      >
        <RichText style={[db.bubbleText, { color: dark ? '#E5E7EB' : '#1F2937' }]} value={beat.text} />
      </View>
    </View>
  );
}
const db = StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'flex-start', marginVertical: 10, gap: 10, width: '100%' } as any,
  rowLeft: { justifyContent: 'flex-start' },
  // `flex-end` seul ne suffisait pas : la bulle occupant toute la largeur disponible, la
  // ligne paraissait alignée à gauche. On borne la bulle (cf. `bubble.maxWidth`) et on pousse
  // le groupe à droite — l'avatar reste EN PREMIER, comme dans le chat.
  rowRight: { justifyContent: 'flex-start', flexDirection: 'row-reverse' },
  charCol: { alignItems: 'center', width: 72, flexShrink: 0 },
  charColLeft: {},
  charColRight: {},
  avatarWrap: { borderWidth: 2.5, overflow: 'hidden' },
  avatarFallback: { justifyContent: 'center', alignItems: 'center' },
  avatarInitial: { color: '#FFF', fontSize: 20, fontWeight: '700' },
  name: { fontSize: 12, fontWeight: '700', marginTop: 4, textAlign: 'center' },
  expression: { fontSize: 10, fontStyle: 'italic', textAlign: 'center', marginTop: 2 },
  bubble: {
    // `flex: 1` étirait la bulle sur toute la largeur : la réplique du joueur ne se
    // distinguait donc pas visuellement de celle d'un PNJ, malgré `justifyContent: flex-end`.
    flexShrink: 1,
    borderWidth: 1.5,
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    padding: 14,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
    elevation: 2,
    maxWidth: '80%',
  },
  bubbleLeft: {},
  bubbleRight: {},
  bubbleText: { fontSize: 15, lineHeight: 22 },
});

function NarrationBeat({ beat, dark }: { beat: SceneBeat; dark: boolean }) {
  return (
    <View style={{ marginVertical: 12, paddingHorizontal: 16, alignItems: 'center' }}>
      <Text style={{ fontSize: 14, fontStyle: 'italic', lineHeight: 22, textAlign: 'center', color: dark ? '#9CA3AF' : '#6B7280' }}>
        {beat.text}
      </Text>
    </View>
  );
}
/** CCT-477 — the diagram sits ABOVE the line it belongs to, which is what the author asked for.
 *  `DiagramViewer` renders nothing at all when there is no usable address, so this wrapper costs
 *  one fragment and never an empty frame. */
function avecDiagramme(beat: SceneBeat, contenu: React.ReactNode) {
  if (!beat.diagramUrl) return contenu;
  return (
    <>
      <DiagramViewer url={beat.diagramUrl} hauteur={240} zoom={beat.diagramZoom} />
      {contenu}
    </>
  );
}

function renderBeat(beat: SceneBeat, dark: boolean, large?: boolean) {
  if (beat.type === 'narration') return avecDiagramme(beat, <NarrationBeat beat={beat} dark={dark} />);
  if (beat.type === 'action') return avecDiagramme(beat, <ActionBeat beat={beat} dark={dark} />);
  return avecDiagramme(beat, <DialogueBeat beat={beat} dark={dark} large={large} />);
}

function renderBeatAnimated(beat: SceneBeat, dark: boolean, onComplete: (audioJoue: boolean) => void, skipRef: React.MutableRefObject<boolean>, onTexteAffiche?: () => void) {
  const commun = { beat, dark, onComplete, onTexteAffiche, skipRef };
  if (beat.type === 'narration') return avecDiagramme(beat, <NarrationBeatAnimated {...commun} />);
  if (beat.type === 'action') return avecDiagramme(beat, <ActionBeatAnimated {...commun} />);
  return avecDiagramme(beat, <DialogueBeatAnimated {...commun} />);
}

// ─── Static replay mode ───────────────────────────────────────────────────────

function StaticScene({
  sceneData,
  sceneNumber,
  title,
  colors,
  isDarkTheme,
  navigation,
  onNext,
  onPrev,
  isLastItem,
}: {
  // Forme SERVIE par le scénario (plus aucune scène codée en dur, cf. CCT-257).
  sceneData: ReturnType<typeof getScenarioSceneData>;
  sceneNumber: number;
  title: string;
  colors: ReturnType<typeof getThemeColors>;
  isDarkTheme: boolean;
  navigation: any;
  onNext?: () => void;
  onPrev?: () => void;
  isLastItem: boolean;
}) {
  const beats = sceneData?.beats ?? [];
  return (
    <SafeAreaView style={[ss.container, { backgroundColor: colors.background }]}>
      <View style={[ss.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={ss.back}>
          <Ionicons name="arrow-back" size={22} color={colors.iconPrimary} />
        </TouchableOpacity>
        <View style={ss.headerCenter}>
          <View style={[ss.badge, { backgroundColor: '#10B981' + '22' }]}>
            <Text style={[ss.badgeText, { color: '#10B981' }]}>Scene {sceneNumber}</Text>
          </View>
          <Text style={[ss.headerTitle, { color: colors.textPrimary }]} numberOfLines={1}>{title}</Text>
        </View>
        <HeaderNav onPrev={onPrev} onNext={onNext} accent={TYPE_COLOR.scene} isLast={isLastItem} />
      </View>
      <ScrollView style={ss.scroll} contentContainerStyle={ss.content}>
        {sceneData?.location ? (
          <View style={[ss.locationCard, { backgroundColor: isDarkTheme ? '#1F2937' : '#F3F4F6', borderColor: isDarkTheme ? '#374151' : '#E5E7EB' }]}>
            <Ionicons name="location" size={14} color={isDarkTheme ? '#9CA3AF' : '#6B7280'} />
            <Text style={[ss.locationText, { color: isDarkTheme ? '#9CA3AF' : '#6B7280' }]}>{sceneData.location}</Text>
          </View>
        ) : null}
        {beats.map((beat, i) => (
          <View key={i}>{renderBeat(beat, isDarkTheme, false)}</View>
        ))}
      </ScrollView>

    </SafeAreaView>
  );
}
const ss = StyleSheet.create({
  container: { flex: 1, minHeight: 0 },
  header: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingVertical: 10, borderBottomWidth: 1, gap: 10 },
  back: { padding: 8 },
  headerCenter: { flex: 1, minWidth: 0, alignItems: 'center', gap: 4 },
  badge: { paddingHorizontal: 10, paddingVertical: 3, borderRadius: 10 },
  badgeText: { fontSize: 11, fontWeight: '700' },
  headerTitle: { fontSize: 14, fontWeight: '600' },
  scroll: { flex: 1, minHeight: 0 },
  content: { padding: 16, paddingBottom: 32 },
  locationCard: { flexDirection: 'row', alignItems: 'flex-start', gap: 8, borderWidth: 1, borderRadius: 8, padding: 12, marginBottom: 16 },
  locationText: { fontSize: 13, fontStyle: 'italic', flex: 1, lineHeight: 18 },
  actionRow: { flexDirection: 'row', justifyContent: 'flex-end', paddingTop: 20, gap: 8 },
  closePill: { paddingHorizontal: 16, paddingVertical: 8, borderRadius: 20, borderWidth: 1, backgroundColor: 'transparent' },
  closePillText: { fontSize: 14, fontWeight: '600' },
  nextPill: { paddingHorizontal: 16, paddingVertical: 8, borderRadius: 20 },
  nextPillText: { color: '#FFF', fontSize: 14, fontWeight: '600' },
});

// ─── Animated cinematic mode ──────────────────────────────────────────────────

export default function SceneReaderScreen({ navigation: navProp, route: routeProp, onProgressConfirmed }: { navigation?: any; route?: any; onProgressConfirmed?: () => void } = {}) {
  const t = useT();
  const navigation = navProp ?? useNavigation<any>();
  const route = routeProp ?? useRoute<any>();
  const { isDarkTheme } = useTheme();
  const colors = getThemeColors(isDarkTheme);
  // La fiche de personnage lisait la palette SOMBRE en dur : carte anthracite et texte clair
  // posés sur un thème clair.
  const C = usePalette();
  const cs = useMemo(() => makeCinemaStyles(C, isDarkTheme), [C, isDarkTheme]);
  // ⚠️ CE HOOK EST ICI, ET PAS PRÈS DE SON USAGE — IL A DÉJÀ CASSÉ L'ÉCRAN. Posé plus bas, il
  // se trouvait APRÈS trois `return` conditionnels (scène vide, voix en attente, vue statique) :
  // React comptait donc un nombre de hooks différent d'un rendu à l'autre et levait « Rendered
  // more hooks than during the previous render » — la scène ne s'ouvrait plus du tout. Un hook
  // ne se place pas où il se lit, il se place où il est TOUJOURS appelé.
  //
  // Il retient le décor dont l'URL est morte : le média peut avoir été supprimé de la Gallery
  // après l'écriture, et un cadre vide par-dessus le décor précédent est plus trompeur que pas
  // de décor du tout.
  const [backdropKo, setBackdropKo] = useState<string | null>(null);
  const { playerName, playerAvatar } = usePlayer();
  // Sync module-level vars so all beat renderers pick up current player info
  // CCT-152 — dans la scène aussi, les répliques du joueur portent SON nom et SON avatar.
  // Le personnage incarné sert au CASTING (fiche des personnages), pas à le renommer.
  _playerName = playerName || 'Joueur';
  _playerAvatar = playerAvatar ?? null;

  // FICHE d'un personnage, ouverte au clic sur son avatar dans un dialogue (CCT-153).
  const [charCard, setCharCard] = useState<any | null>(null);
  useEffect(() => {
    _openCharCard = (rawName: string) => {
      const chars: any[] = (getScenarioForMission(Number(route.params?.missionId)) as any)?.characters ?? [];
      const found = chars.find((c) => String(c?.name ?? '').toLowerCase() === rawName.toLowerCase());
      if (found) setCharCard(found);
    };
    return () => { _openCharCard = null; };   // pas de fuite entre écrans
  }, [route.params?.missionId]);

  const { sceneNumber, title } = route.params ?? {};
  const missionId = Number(route.params?.missionId) || undefined;
  const _storyKeyMap: Record<number, string> = {
    1: 'egocrisis', 2: 'foundation_gamble', 3: 'data_storm',
    102: 'architecture_under_fire', 201: 'architecture_under_fire',
    109: 'cloud_heist', 110: 'neural_override', 111: 'pipeline_inferno',
    104: 'quantum_gambit', 105: 'learning_machine', 106: 'ink_protocol',
    108: 'arabic_modern', 112: 'arabic_modern', 113: 'arabic_modern',
    114: 'arabic_modern', 115: 'arabic_modern',
  };
  const storyKey = _storyKeyMap[missionId ?? 0] ?? 'egocrisis';
  _currentStoryKey = storyKey;
  _missionId = missionId ?? 0;
  // Personnage joué : lu APRÈS les params (il dépend de `missionId`). `isPlayerSpeaker` sait
  // relire tardivement si le scénario n'était pas encore chargé à ce rendu.
  _playerCharName = getMissionPlayer(_missionId)?.name ?? null;
  const sceneKey = `${missionId ?? 0}-${route.params?.itemId ?? `${route.params?.chapterId}-${sceneNumber}`}`;
  // PLUS DE REPLI sur `getSceneData` (CCT-257) : quand la scène du scénario n'était pas
  // trouvée, l'écran jouait une scène CODÉE EN DUR — l'arrivée de Raoul chez EgoCrisis, avec
  // Youssef, Anna, Luc et Emilie, des personnages d'aucune mission. Le joueur lisait donc une
  // AUTRE histoire sous le bon titre, sans le moindre signal. Un écran vide qui se voit vaut
  // mieux qu'un récit crédible qui n'est pas le sien.
  const sceneData = getScenarioSceneData(
    missionId, route.params?.chapterId, sceneNumber, route.params?.itemId,
  );
  const beats = sceneData?.beats ?? [];

  // VOIX PROPRE À LA PARTIE (CCT-310). Une scène qui contient des répliques nommant le joueur
  // n'est jouable qu'une fois leur rendu TERMINÉ : la moitié doublée et l'autre muette ne se
  // lit pas comme un chargement, ça se lit comme une panne.
  //
  // ⚠️ ET L'ATTENTE EST BORNÉE, des deux côtés. Le serveur compte un rendu coupé par le plafond
  // de dépense comme terminé ; le hook rend la main au bout de vingt secondes quoi qu'il
  // arrive. Une mission figée sur un sablier est pire qu'une mission lue à l'écran.
  const { getAccessToken } = useAuth();
  const [sceneLoad, setSceneLoad] = useState<'loading' | 'ready' | 'error'>('loading');
  const [reloadAttempt, setReloadAttempt] = useState(0);
  useEffect(() => {
    let cancelled = false;
    if (getScenarioSceneData(missionId, route.params?.chapterId, sceneNumber, route.params?.itemId)?.beats.length) {
      setSceneLoad('ready');
      return;
    }
    setSceneLoad('loading');
    void (async () => {
      try {
        const token = getAccessToken();
        if (!token || !missionId) throw new Error('Session unavailable');
        await loadRuntimeScenario(Number(missionId),
          () => apiFetch(`/me/stories/${missionId}/scenario`, { token, auth: true }), true);
        if (!cancelled) setSceneLoad('ready');
      } catch {
        if (!cancelled) setSceneLoad('error');
      }
    })();
    return () => { cancelled = true; };
  }, [missionId, route.params?.itemId, route.params?.chapterId, sceneNumber, reloadAttempt, getAccessToken]);
  const voixPrete = useRunAudioReady(
    getAccessToken(), missionId, sceneData?.audioPending === true);
  _streamItemId = sceneData?.itemId ?? '';
  _streamToken = getAccessToken();

  const alreadySeen = seenScenes.has(sceneKey);

  const [currentIdx, setCurrentIdx] = useState(0);
  const [isComplete, setIsComplete] = useState(alreadySeen);
  const [showStatic, setShowStatic] = useState(alreadySeen);

  const fadeAnim = useRef(new Animated.Value(alreadySeen ? 1 : 0)).current;
  const [isTextComplete, setIsTextComplete] = useState(alreadySeen);
  /**
   * Is the WHOLE line readable? Not the same question as `isTextComplete`, which for a voiced
   * line only becomes true when the SOUND ends.
   *
   * ⚠️ AUTOPLAY STILL FOLLOWS THE SOUND. This one governs what the PLAYER may do: see the
   * chevron, and move on by tapping. The cinema pacing is unchanged for whoever lets it run.
   */
  const [texteAffiche, setTexteAffiche] = useState(alreadySeen);
  // Le son de CE beat a-t-il reellement joue ? Determine la cadence d'enchainement.
  const [audioJoue, setAudioJoue] = useState(false);
  const skipRef = useRef<boolean>(false);

  const animateIn = useCallback(() => {
    fadeAnim.setValue(0);
    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: 350,
      useNativeDriver: Platform.OS !== 'web',
    }).start();
  }, [fadeAnim]);

  useEffect(() => {
    if (!alreadySeen) animateIn();
  }, [currentIdx, alreadySeen, animateIn]);

  useEffect(() => {
    if (!alreadySeen) {
      skipRef.current = false;
      setIsTextComplete(false);
      setTexteAffiche(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIdx]);

  const handlePrev = () => {
    setAutoplay(false);                 // retour arriere = reprise en main explicite
    if (currentIdx > 0) {
      skipRef.current = true;   // les beats déjà vus s'affichent d'emblée, sans re-jouer la frappe
      setIsTextComplete(true);
      setTexteAffiche(true);
      setCurrentIdx(prev => prev - 1);
    }
  };

  const handleNext = () => {
    // ⚠️ AVANCER NE COUPE PAS L'ENCHAINEMENT. Un appui vers l'avant veut dire « plus vite »,
    // pas « je reprends la main » : couper ici obligerait a cliquer toutes les repliques
    // suivantes pour avoir voulu presser UNE fois.
    if (currentIdx < beats.length - 1) {
      skipRef.current = false;
      setIsTextComplete(false);
      setTexteAffiche(false);
      setCurrentIdx(prev => prev + 1);
    } else {
      seenScenes.add(sceneKey);
      setIsComplete(true);
      setShowStatic(true);
    }
  };

  const itemNav = useVisitedItemNav(route, navigation, undefined, onProgressConfirmed);
  // Progression PILOTEE PAR LE MODELE (cf. utils/elementProgress) : c'est l'element
  // AFFICHE qui est enregistre, quel que soit le chemin emprunte pour l'ouvrir.
  useElementVisited(route?.params?.missionId, route?.params?.itemId, beats.length > 0);

  // ⚠️ L'ENCHAINEMENT S'ARRETE DES QU'ON Y TOUCHE. Un defilement qui reprend la main apres
  // un retour arriere ferait repartir le lecteur en avant contre sa volonte — le pire des
  // comportements pour quelqu'un qui vient de vouloir relire.
  const [autoplay, setAutoplay] = useState(true);
  const last = currentIdx >= beats.length - 1;

  // ⚠️ UN BEAT NE DOIT JAMAIS POUVOIR RESTER BLOQUE.
  //
  // L'enchainement attend `isTextComplete`, qui vient d'un minuteur de frappe ou d'une fin de
  // son. Si ce signal n'arrive pas — quelle qu'en soit la raison — l'screen s'immobilise sans
  // rien afficher d'anormal : ni erreur, ni indicateur. C'est exactement ce qui a ete
  // signale, « l'screen bloque et le loader ne finit pas », et la telemetrie n'en a rien vu :
  // zero erreur HTTP, zero erreur console, parce qu'il ne se passe RIEN.
  //
  // On borne donc l'attente par la duree de frappe attendue, plus une marge. Passe ce delai
  // le beat est declare affiche : la scene continue, et l'anomalie est TRACEE.
  // MECANISME GENERAL DE VIVACITE (CCT-226) : l'screen DECLARE ce qu'il attend. Si l'attente
  // ne se resout pas, l'anomalie est emise — c'est ce qui manquait, et pas seulement ici.
  useLiveness({
    screen: 'SceneReader',
    expected: `beat:${currentIdx}`,
    active: !isTextComplete && !showStatic && !!beats[currentIdx],
    context: { total: beats.length, type: beats[currentIdx]?.type,
                avec_audio: !!beats[currentIdx]?.audio?.key },
  });

  useEffect(() => {
    if (isTextComplete || showStatic || !beats[currentIdx]) return;
    const mots = String(beats[currentIdx].text || '').trim().split(/\s+/).filter(Boolean).length;
    const frappe = beats[currentIdx].audio?.key
      ? (beats[currentIdx].audio!.duree || 0) * 1000
      : mots * 30;
    const t = setTimeout(() => {
      console.warn('[scene] beat fige, on debloque :', currentIdx, beats[currentIdx]?.type);
      journaliserBlocage(currentIdx, beats.length, beats[currentIdx]?.type);
      setIsTextComplete(true);
      setTexteAffiche(true);
    }, frappe + 6000);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIdx, isTextComplete, showStatic]);

  // Etat observable depuis la console : `__ccScene()`. Sans lui, « l'screen bloque » ne se
  // distingue pas de « l'enchainement est desactive » ni de « le beat n'a jamais fini » —
  // trois causes, un seul symptome, et aucune trace dans les logs puisque rien n'echoue.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    (window as any).__ccScene = () => ({
      beat: currentIdx + 1,
      total: beats.length,
      type: beats[currentIdx]?.type ?? null,
      text: String(beats[currentIdx]?.text || '').slice(0, 60),
      audio: beats[currentIdx]?.audio?.key ?? null,
      textShown: texteAffiche,
      soundFinished: isTextComplete,
      soundPlayed: audioJoue,
      autoplay: autoplay,
      staticView: showStatic,
      waitMs: tempsAvantSuite(beats[currentIdx], true, audioJoue),
    });
  });

  useEffect(() => {
    if (!autoplay || !isTextComplete || last || showStatic) return;
    const pending = tempsAvantSuite(beats[currentIdx], true, audioJoue);
    const t = setTimeout(() => {
      skipRef.current = false;
      setIsTextComplete(false);
      setTexteAffiche(false);
      setCurrentIdx((i) => Math.min(i + 1, beats.length - 1));
    }, pending);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoplay, isTextComplete, currentIdx, last, showStatic, audioJoue]);

  // Scène INTROUVABLE dans le scénario servi : on le DIT. Auparavant l'écran repliait sur une
  // scène bundlée, donc sur une autre histoire, sous le bon titre (CCT-257).
  if (!sceneData || beats.length === 0) {
    return (
      <View style={[cs.container, { backgroundColor: isDarkTheme ? '#0D1117' : C.bg, justifyContent: 'center', alignItems: 'center', padding: 32 }]}>
        {sceneLoad === 'loading' ? <ActivityIndicator size="large" color={C.cyan} />
          : <Ionicons name="document-outline" size={44} color={C.textSec} />}
        <Text style={{ color: '#E5E7EB', fontSize: 16, fontWeight: '700', marginTop: 12, textAlign: 'center' }}>
          {title || t('scene.title')}
        </Text>
        <Text style={{ color: '#9CA3AF', fontSize: 13, marginTop: 8, textAlign: 'center', lineHeight: 19 }}>
          {sceneLoad === 'loading' ? t('chapter.loading') : t('scene.noText')}
        </Text>
        {sceneLoad !== 'loading' && <TouchableOpacity accessibilityRole="button"
          onPress={() => setReloadAttempt(n => n + 1)}
          style={{ backgroundColor: '#4F46E5', paddingHorizontal: 16, paddingVertical: 8, borderRadius: 20, marginTop: 20 }}>
          <Text style={{ color: '#FFF', fontSize: 14, fontWeight: '600' }}>{t('common.retry')}</Text>
        </TouchableOpacity>}
        <TouchableOpacity onPress={() => navigation.goBack()}
          style={{ backgroundColor: '#4F46E5', paddingHorizontal: 16, paddingVertical: 8, borderRadius: 20, marginTop: 20 }}>
          <Text style={{ color: '#FFF', fontSize: 14, fontWeight: '600' }}>{t('common.back')}</Text>
        </TouchableOpacity>
      </View>
    );
  }

  // LA SCÈNE ATTEND SA VOIX (CCT-310). Elle contient des répliques qui nomment le joueur et
  // dont le rendu, fait EN FOND au lancement de la partie, n'est pas terminé.
  //
  // ⚠️ ON ATTEND AVANT DE COMMENCER, PAS PENDANT. Laisser la scène démarrer produirait les
  // premières répliques muettes et les suivantes doublées : le joueur n'y lirait pas un
  // chargement, il y lirait une panne — et il n'y a aucun moyen de rattraper un beat déjà passé.
  //
  // L'attente est bornée des deux côtés (verdict serveur, plus vingt secondes ici) : au pire,
  // la scène se joue muette. Le son est un enrichissement, pas une condition.
  if (!voixPrete && !alreadySeen && !showStatic) {
    return (
      <View style={[cs.container, { backgroundColor: isDarkTheme ? '#0D1117' : C.bg,
                                    alignItems: 'center', justifyContent: 'center' }]}>
        <ActivityIndicator size="large" color={C.cyan} />
        <Text style={{ color: C.textSec, marginTop: 14, fontSize: 14 }}>
          {t('scene.preparingVoices')}
        </Text>
      </View>
    );
  }

  if (showStatic) {
    return (
      <StaticScene
        sceneData={sceneData}
        sceneNumber={sceneNumber}
        title={title ?? sceneData?.title ?? ''}
        colors={colors}
        isDarkTheme={isDarkTheme}
        navigation={navigation}
        onNext={itemNav.goNext}
        onPrev={itemNav.goPrev}
        isLastItem={itemNav.isLast}
      />
    );
  }

  const currentBeat = beats[currentIdx];
  // ⚠️ LES DEUX BRANCHES ÉTAIENT SOMBRES : `#0D1117` et `#111827` sont deux nuits. Le lecteur
  // de scènes ne pouvait donc PAS suivre le thème clair, quoi qu'on règle ailleurs.
  const BG = isDarkTheme ? '#0D1117' : C.bg;

  // DÉCOR DE LA SCÈNE (CCT-313).
  //
  // ⚠️ ON NE REFAIT PAS LA MARCHE D'HÉRITAGE ICI. Le serveur a déjà résolu, pour CHAQUE beat, le
  // décor en vigueur — une réplique muette porte celui de la précédente, une réplique qui
  // l'efface porte `null`. La recalculer ici donnerait deux définitions de la même règle, et
  // l'écart entre la prévisualisation de l'auteur et l'écran du joueur ne se verrait qu'en
  // production.
  //
  // `?? sceneData?.backdropUrl` sert AVANT le premier beat : sans lui l'écran s'ouvre nu et le
  // décor n'arriverait qu'au beat suivant, ce qui se lit comme un raté d'affichage.
  // ⚠️ `??` ENTRE LE BEAT ET LA SCÈNE RESSUSCITAIT LE DÉCOR EFFACÉ. Une réplique qui EFFACE
  // porte `backdropUrl: null` — et `null ?? décorDeLaScène` rend le décor de la scène. « Effacer »
  // devenait donc « revenir au décor par défaut », l'exact contraire de ce que l'auteur a écrit.
  // MESURÉ à l'écran : au beat 9/9, alors que la réplique 7 efface, le décor de la scène était
  // toujours affiché.
  //
  // Le décor de la scène ne sert QUE tant qu'aucun beat n'est affiché. Dès qu'il y en a un, sa
  // valeur fait foi — y compris quand cette valeur est « aucun ».
  const voulu = currentBeat ? (currentBeat.backdropUrl ?? null) : (sceneData?.backdropUrl ?? null);
  const backdrop = voulu && voulu !== backdropKo ? voulu : null;

  return (
    <View style={[cs.container, { backgroundColor: BG }]}>
      {/* ⚠️ EN FOND, DERRIÈRE LE TEXTE, ET JAMAIS INTERACTIF. `pointerEvents="none"` : la scène
          s'avance en tapant l'écran, et une image qui capte le tapotement bloquerait la lecture
          sans que rien ne l'explique. Un `onError` qui EFFACE : une URL morte laisserait sinon
          un cadre vide par-dessus le décor précédent, plus trompeur que pas de décor du tout. */}
      {backdrop ? (
        <View style={cs.backdrop} pointerEvents="none">
          <Image
            key={backdrop}
            source={{ uri: backdrop }}
            style={StyleSheet.absoluteFill}
            resizeMode="cover"
            onError={() => setBackdropKo(backdrop)}
          />
        </View>
      ) : null}
      {/* Header */}
      <SafeAreaView>
        <View style={cs.header}>
          <View style={{ flex: 1, minWidth: 0 }}>
          <ElementHeader
            missionTitle={(route.params as any)?.missionTitle ?? ''}
            chapterTitle={(route.params as any)?.chapterTitle ?? ''}
            elementTitle={`Scene ${sceneNumber} — ${sceneData?.title ?? title}`}
            onBack={() => navigation.goBack()}
            badge={<Text style={cs.progress}>{currentIdx + 1}/{beats.length}</Text>}
          />
          </View>
          {/* Modèle commun à tous les éléments : UN retour (timeline) à gauche, les flèches de
              navigation à droite. Elles parcourent d'abord les beats et ne franchissent
              l'élément qu'aux extrémités. « → » reste inactive tant que le texte S'ÉCRIT — un
              appui sur la zone de texte saute l'animation. ⚠️ Elle attendait auparavant la FIN
              DU SON : sur une réplique doublée, dont le texte est entier dès la première image,
              la flèche restait grisée pendant toute la durée de la voix. */}
          <HeaderNav
            onPrev={currentIdx > 0 ? handlePrev : itemNav.goPrev}
            onNext={texteAffiche ? handleNext : undefined}
            accent={TYPE_COLOR.scene}
            isLast={currentIdx === beats.length - 1}
          />
        </View>
      </SafeAreaView>

      {/* Location card — first beat only */}
      {currentIdx === 0 && sceneData?.location ? (
        <View style={cs.locationCard}>
          <Text style={cs.locationText}>{sceneData.location}</Text>
        </View>
      ) : null}

      {/* Beat area — tappable */}
      <TouchableOpacity
        style={cs.beatArea}
        onPress={() => {
          // UN APPUI FAIT TOUJOURS AVANCER, et ne coupe JAMAIS l'enchainement.
          //
          // Pendant la frappe, il l'acheve ; une fois le texte entier affiche — donc pendant
          // la pause de lecture d'une narration — il passe a la suite sans attendre la fin du
          // temps de lecture. C'est tout l'interet de cette pause : elle est genereuse par
          // defaut pour qui lit, et supprimable d'un geste pour qui a fini.
          // ⚠️ THE TEST IS "HAVE I READ IT", NOT "HAS THE SOUND FINISHED". It used to be the
          // latter: on a voiced line the text was already there in full, so this branch wrote
          // `skipRef` — a ref, which re-renders nothing — and the tap did literally nothing for
          // the whole duration of the sound. MEASURED: 14.55 s and 12.96 s on Ego Crisis 2.
          // Advancing unmounts the beat, and `useLineAudio`'s cleanup stops the voice.
          if (!texteAffiche) {
            skipRef.current = true;
          } else {
            handleNext();
          }
        }}
        activeOpacity={1}
      >
        <Animated.View style={[cs.beatContent, { opacity: fadeAnim }]}>
          {currentBeat
            ? renderBeatAnimated(currentBeat, isDarkTheme,
                                 (joue: boolean) => { setAudioJoue(joue); setIsTextComplete(true); },
                                 skipRef,
                                 () => setTexteAffiche(true))
            : null}
        </Animated.View>
      </TouchableOpacity>

      {/* Bottom bar */}
      <SafeAreaView>
        <View style={cs.bottomBar}>
          {/* Progress dots */}
          <View style={cs.dots}>
            {beats.map((_, i) => (
              <View
                key={i}
                style={[
                  cs.dot,
                  {
                    backgroundColor: i < currentIdx ? '#10B981' : i === currentIdx ? C.textPrim : C.border,
                    width: i === currentIdx ? 16 : 6,
                  },
                ]}
              />
            ))}
          </View>
          {/* Zelda-style "continue" cue: only once the line has finished typing and another beat
              awaits — a scene that ends, or one still typing, shows nothing. */}
          {texteAffiche && !last && !showStatic && <AdvanceChevron color={C.textSec} />}
        </View>
      </SafeAreaView>

      {/* FICHE du personnage (CCT-153) : un dialogue ne disait rien de qui parle — nom, rôle
          et description authorée sont maintenant à un clic sur l'avatar. */}
      {!!charCard && (
        <TouchableOpacity activeOpacity={1} onPress={() => setCharCard(null)}
          style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0,
            backgroundColor: 'rgba(0,0,0,0.6)', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <View style={{ width: '100%', maxWidth: 420, borderRadius: 16, padding: 20, gap: 10,
            backgroundColor: C.card, borderWidth: 1, borderColor: C.border }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
              {!!charCard.avatarUrl && (
                <Image source={{ uri: charCard.avatarUrl }}
                  style={{ width: 56, height: 56, borderRadius: 28 }} resizeMode="cover" />
              )}
              <View style={{ flex: 1 }}>
                <Text style={{ color: C.textPrim, fontSize: 16, fontWeight: '700' }}>{charCard.name}</Text>
                <Text style={{ color: C.textSec, fontSize: 13 }}>{charCard.role}</Text>
                {!!charCard.isPlayer && (
                  <Text style={{ color: C.cyan, fontSize: 12, marginTop: 2 }}>
                    Rôle tenu par {_playerName}
                  </Text>
                )}
              </View>
            </View>
            {!!charCard.description && (
              <Text style={{ color: C.textSec, fontSize: 13, lineHeight: 19 }}>{charCard.description}</Text>
            )}
            <TouchableOpacity onPress={() => setCharCard(null)} style={{ alignSelf: 'flex-end', padding: 8 }}>
              <Text style={{ color: C.cyan, fontWeight: '600' }}>{t('common.close')}</Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      )}
    </View>
  );
}

// Feuille du lecteur de scènes : elle était peinte pour un fond NUIT (flèche blanche, jauge en
// blanc translucide, carte de lieu en voile clair). Posée sur un fond clair, tout disparaissait.
const makeCinemaStyles = (C: Palette, dark: boolean) => StyleSheet.create({
  container: { flex: 1 },
  // DÉCOR (CCT-313) : il occupe tout l'écran, DERRIÈRE le texte, et ne capte rien.
  //
  // ⚠️ LE VOILE N'EST PAS DÉCORATIF, ET SA VALEUR A ÉTÉ JUGÉE À L'ÉCRAN. Une photo pleine page
  // derrière du texte ne le rend pas seulement moins lisible : elle lui dispute l'ATTENTION.
  // Mesuré sur une capture réelle, à 0,28, avec un média portant du texte incrusté — la photo
  // se lisait comme un troisième interlocuteur, à parité avec la réplique. À 0,12 elle redevient
  // une atmosphère, et le dialogue domine.
  //
  // ⚠️ LE THÈME CLAIR PARDONNE MOINS. Un fond pâle sous du texte foncé perd son contraste plus
  // vite qu'un fond sombre sous du texte clair : d'où une valeur ENCORE plus basse, et non la
  // même des deux côtés.
  //
  // La bulle de dialogue a son propre fond : ce réglage ne joue donc pas la LISIBILITÉ du texte
  // (elle est acquise), mais l'équilibre entre le décor et ce qu'il y a à lire.
  backdrop: { ...StyleSheet.absoluteFillObject, opacity: dark ? 0.12 : 0.08 },
  header: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 12, gap: 10 },
  backBtn: { padding: 6 },
  badge: { flex: 1, paddingHorizontal: 10, paddingVertical: 4, borderRadius: 10, alignItems: 'center' },
  badgeText: { color: '#10B981', fontSize: 12, fontWeight: '700' },
  progress: { color: C.textSec, fontSize: 13, fontWeight: '600', minWidth: 30, textAlign: 'right' },
  locationCard: {
    marginHorizontal: 16,
    marginBottom: 8,
    backgroundColor: dark ? 'rgba(255,255,255,0.05)' : C.card,
    borderLeftWidth: 3,
    borderLeftColor: '#6B7280',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 4,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
  },
  locationText: { color: C.textSec, fontSize: 13, fontStyle: 'italic', lineHeight: 18 },
  beatArea: { flex: 1, justifyContent: 'center', paddingHorizontal: 16 },
  beatContent: { width: '100%' },
  bottomBar: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 12, gap: 12 },
  dots: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 4 },
  dot: { height: 6, borderRadius: 3 },
});










